import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
} from '@goodday-clone/ui'
import {
  Search,
  Maximize2,
  ArrowUpDown,
  SlidersHorizontal,
  ListFilter,
  User,
  List,
  Flame,
  Snowflake,
  Megaphone,
  FolderOpen,
  Building,
  Globe,
  Contact,
  Users,
  Plus,
  CheckCircle2,
  AlertCircle,
  X,
  Check,
  ArrowLeft,
} from 'lucide-react'
import { LeadDetailModal, LeadDetailData } from '@/components/sales/lead-detail-modal'
import { AddLeadModal } from '@/components/sales/add-lead-modal'
import { api } from '@/api/client'
import { useLeadSources, useLeadStatuses } from '@/shared/hooks/useCrmVocabulary'
import { LeadsSubNav } from '@/features/leads/LeadsSubNav'
import { cn, cardClassName } from '@jira-clone/ui'

// Status column definitions

// Sources and statuses come from the org's configured CRM vocabulary. This
// page used to hardcode 6 sources and 4 statuses — it was missing "LinkedIn"
// and "Cold Outreach" entirely, and coerced any unrecognised source to
// "Website", so those leads silently showed up in the wrong row.
interface LeadCardData {
  id: string
  name: string
  company?: string
  contactPerson?: string
  interest: 'Cold' | 'Warm' | 'Hot'
  source: string
  status: string
  number?: string
  value?: number
  createdAt?: string
  hasAvatars?: boolean
  convertedDealId?: string
  assignee?: string
  email?: string
  phone?: string
  notes?: string
  mapsUrl?: string
  rating?: number | null
}

type SortOption =
  | 'default'
  | 'date-desc'
  | 'date-asc'
  | 'name-asc'
  | 'name-desc'
  | 'value-desc'
  | 'value-asc'
  | 'interest-desc'

const SORT_OPTIONS: { id: SortOption; label: string }[] = [
  { id: 'default', label: 'Default Order' },
  { id: 'date-desc', label: 'Newest to Oldest' },
  { id: 'date-asc', label: 'Oldest to Newest' },
  { id: 'name-asc', label: 'Name (A to Z)' },
  { id: 'name-desc', label: 'Name (Z to A)' },
  { id: 'value-desc', label: 'Price: High to Low' },
  { id: 'value-asc', label: 'Price: Low to High' },
  { id: 'interest-desc', label: 'Interest: Hot to Cold' },
]

export default function LeadsBySourcePage() {
  const navigate = useNavigate()
  const { data: leadSources = [] } = useLeadSources()
  const { data: leadStatuses = [] } = useLeadStatuses()
  // The matrix used to be a fixed 4-status grid; it now widens to however many
  // statuses the organization has configured.
  const gridTemplate = `190px repeat(${Math.max(leadStatuses.length, 1)}, minmax(270px, 1fr))`

  const [leads, setLeads] = useState<LeadCardData[]>([])

  const displaySources = useMemo(() => {
    const existingNames = new Set(leadSources.map((s) => s.name))
    const extraSources: { id: string; name: string }[] = []
    leads.forEach((l) => {
      if (l.source && !existingNames.has(l.source)) {
        existingNames.add(l.source)
        extraSources.push({ id: `extra-${l.source}`, name: l.source })
      }
    })
    return [...leadSources, ...extraSources]
  }, [leadSources, leads])
  const [selectedLeadData, setSelectedLeadData] = useState<LeadDetailData | null>(null)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [notification, setNotification] = useState<{
    type: 'success' | 'error'
    message: string
  } | null>(null)

  // Search feature state
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Sort feature state
  const [sortBy, setSortBy] = useState<SortOption>('default')
  const [isSortOpen, setIsSortOpen] = useState(false)
  const sortDropdownRef = useRef<HTMLDivElement>(null)

  // Click outside to close sort dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setIsSortOpen(false)
      }
    }
    if (isSortOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isSortOpen])

  const filteredLeads = useMemo(() => {
    // 1. Filter
    let result = leads
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim()
      result = result.filter((l) => {
        return (
          l.name?.toLowerCase().includes(q) ||
          l.company?.toLowerCase().includes(q) ||
          l.contactPerson?.toLowerCase().includes(q) ||
          l.source?.toLowerCase().includes(q) ||
          l.status?.toLowerCase().includes(q) ||
          l.interest?.toLowerCase().includes(q) ||
          l.number?.toLowerCase().includes(q)
        )
      })
    }

    // 2. Sort
    if (sortBy === 'default') return result

    const sorted = [...result]
    switch (sortBy) {
      case 'date-desc':
        return sorted.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
      case 'date-asc':
        return sorted.sort((a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime())
      case 'name-asc':
        return sorted.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
      case 'name-desc':
        return sorted.sort((a, b) => (b.name || '').localeCompare(a.name || '', undefined, { sensitivity: 'base' }))
      case 'value-desc':
        return sorted.sort((a, b) => (b.value || 0) - (a.value || 0))
      case 'value-asc':
        return sorted.sort((a, b) => (a.value || 0) - (b.value || 0))
      case 'interest-desc': {
        const rank: Record<string, number> = { Hot: 3, Warm: 2, Cold: 1 }
        return sorted.sort((a, b) => (rank[b.interest] || 0) - (rank[a.interest] || 0))
      }
      default:
        return sorted
    }
  }, [leads, searchQuery, sortBy])

  // Load leads from the GoodDay REST API
  useEffect(() => {
    async function loadBackendLeads() {
      try {
        const data = await api.get<any>('/leads')
        const rawLeads = Array.isArray(data) ? data : (data?.data || data?.leads || [])

        if (Array.isArray(rawLeads) && rawLeads.length > 0) {
          setLeads((prevLeads) => {
            const merged = [...prevLeads]
            rawLeads.forEach((l: any, idx: number) => {
              const alreadyExists = merged.some(
                (m) => m.name.toLowerCase() === l.name.toLowerCase()
              )
              if (!alreadyExists) {
                // Values are already canonical (validated server-side), so
                // they're used as-is rather than being coerced.
                const normSource: string = l.source || 'None'
                const normStatus: string = l.status || 'New'

                let normInterest: 'Cold' | 'Warm' | 'Hot' = 'Warm'
                if (l.interest === 'Cold' || l.interest === 'Hot') {
                  normInterest = l.interest
                }

                const leadValue = typeof l.value === 'number'
                  ? l.value
                  : (l.value ? parseFloat(String(l.value).replace(/[^0-9.]/g, '')) || 0 : 0)
                const leadCreatedAt = l.createdAt || l.created_at || (l.date ? new Date(l.date).toISOString() : new Date(Date.now() - idx * 3600000).toISOString())

                merged.push({
                  id: l._id || l.id || `lead-dyn-${idx}`,
                  name: l.name,
                  company: l.company || undefined,
                  contactPerson: l.role || undefined,
                  interest: normInterest,
                  source: normSource,
                  status: normStatus,
                  number: `#${30 + idx}`,
                  hasAvatars: Boolean(l.assignee),
                  assignee: l.assignee || '',
                  email: l.email || '',
                  phone: l.phone || '',
                  notes: l.notes || '',
                  mapsUrl: l.mapsUrl || '',
                  rating: typeof l.rating === 'number' ? l.rating : (l.rating ? parseFloat(l.rating) : null),
                  convertedDealId: l.convertedDealId,
                  value: leadValue,
                  createdAt: leadCreatedAt,
                })
              }
            })
            return merged
          })
        }
      } catch (err) {
        console.warn('Backend leads sync note:', err)
      }
    }
    loadBackendLeads()
  }, [])

  const handleCardClick = (lead: LeadCardData) => {
setSelectedLeadData({
        id: lead.id,
        name: lead.name,
        company: lead.company,
        contactPerson: lead.contactPerson,
        source: lead.source,
        status: lead.status === 'qualified' ? 'Qualified' : lead.status === 'attempting_contact' ? 'Attempting Contact' : lead.status === 'unqualified' ? 'Unqualified' : lead.status || 'New',
        interest: lead.interest,
        rating: lead.rating ?? null,
        assignee: lead.assignee,
        number: lead.number,
        email: lead.name === 'RBM' ? 'mrunmaikudale18@gmail.com' : `${lead.name.toLowerCase().replace(/\s+/g, '.')}@example.com`,
        phone: lead.name === 'RBM' ? '8975929541' : '555-019-2834',
        value: lead.value ? `₹${lead.value.toLocaleString()}` : '',
        notes: lead.name === 'RBM' ? 'Testing' : (lead.notes || ''),
        mapsUrl: lead.mapsUrl,
        convertedDealId: lead.convertedDealId,
    })
    setIsDetailModalOpen(true)
  }

  const handleLeadUpdate = (updatedLead: LeadDetailData) => {
    setLeads((prev) =>
      prev.map((l) => {
        if (l.id === updatedLead.id || l.name.toLowerCase() === updatedLead.name.toLowerCase()) {
          const normStatus: string = updatedLead.status || 'New'
          const normSource: string = updatedLead.source || 'None'

          let normInterest: 'Cold' | 'Warm' | 'Hot' = 'Warm'
          if (updatedLead.interest === 'Cold' || updatedLead.interest === 'Hot') {
            normInterest = updatedLead.interest
          }

          const parsedValue = typeof updatedLead.value === 'number'
            ? updatedLead.value
            : (updatedLead.value ? parseFloat(String(updatedLead.value).replace(/[^0-9.]/g, '')) || l.value : l.value)

          return {
            ...l,
            name: updatedLead.name,
            company: updatedLead.company,
            contactPerson: updatedLead.contactPerson,
            source: normSource,
            status: normStatus,
            interest: normInterest,
            value: parsedValue,
          }
        }
        return l
      })
    )
  }

  const handleLeadCreated = (newLead: LeadDetailData) => {
    const normSource: string = newLead.source || 'None'
    const normStatus: string = newLead.status || 'New'

    let normInterest: 'Cold' | 'Warm' | 'Hot' = 'Warm'
    if (newLead.interest === 'Cold' || newLead.interest === 'Hot') {
      normInterest = newLead.interest
    }

    const leadValue = typeof newLead.value === 'number'
      ? newLead.value
      : (newLead.value ? parseFloat(String(newLead.value).replace(/[^0-9.]/g, '')) || 0 : 0)

    const cardData: LeadCardData = {
      id: newLead.id,
      name: newLead.name,
      company: newLead.company,
      contactPerson: newLead.contactPerson,
      source: normSource,
      status: normStatus,
      interest: normInterest,
      number: newLead.number ? `#${newLead.number}` : `#${Math.floor(Math.random() * 90) + 10}`,
      hasAvatars: true,
      assignee: newLead.assignee || '',
      email: newLead.email || '',
      phone: newLead.phone || '',
      notes: newLead.notes || '',
      value: leadValue,
      createdAt: new Date().toISOString(),
    }

    setLeads((prev) => [cardData, ...prev])
    setNotification({
      type: 'success',
      message: `Lead "${newLead.name}" added to matrix!`,
    })
    setTimeout(() => setNotification(null), 5000)
  }

  const getInterestIcon = (interest: 'Cold' | 'Warm' | 'Hot') => {
    switch (interest) {
      case 'Cold':
        return <Snowflake className="h-3 w-3 text-sky-500 shrink-0" />
      case 'Warm':
        return <Flame className="h-3 w-3 text-amber-500 shrink-0" />
      case 'Hot':
        return <Flame className="h-3 w-3 text-rose-500 shrink-0" />
    }
  }

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'Advertising':
        return <Megaphone className="h-3 w-3 text-muted-foreground shrink-0" />
      case 'Other':
        return <FolderOpen className="h-3 w-3 text-muted-foreground shrink-0" />
      case 'Referral':
        return <Users className="h-3 w-3 text-muted-foreground shrink-0" />
      case 'Trade Show':
        return <Building className="h-3 w-3 text-muted-foreground shrink-0" />
      case 'Website':
        return <Globe className="h-3 w-3 text-muted-foreground shrink-0" />
      default:
        return <List className="h-3 w-3 text-muted-foreground shrink-0" />
    }
  }

  return (
    <div className="space-y-6 max-w-full mx-auto pb-8">
      {/* Notifications */}
      {notification && (
        <div
          className={`p-3 rounded-lg border flex items-center justify-between text-xs transition-all ${
            notification.type === 'success'
              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300'
              : 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300'
          }`}
        >
          <div className="flex items-center gap-2">
            {notification.type === 'success' ? (
              <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
            ) : (
              <AlertCircle className="h-4 w-4 shrink-0 text-amber-500" />
            )}
            <span>{notification.message}</span>
          </div>
          <button
            onClick={() => setNotification(null)}
            className="text-muted-foreground hover:text-foreground ml-4"
          >
            ✕
          </button>
        </div>
      )}

      {/* 1. PAGE HEADER & SECTION TABS (same layout as the other Leads pages) */}
      <div className="flex flex-col gap-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-[#6825F5] to-[#7C3AED] text-white shadow-sm shadow-purple-200 transition-all hover:shadow-md hover:shadow-purple-300/50"
              aria-label="Back"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Leads By Source</h1>
              <p className="text-sm text-muted-foreground">
                Lead volume across pipeline stages, grouped by acquisition source
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => setIsAddModalOpen(true)} className="gap-1.5 shadow-xs">
              <Plus className="h-4 w-4 mr-1" /> Add Lead
            </Button>
          </div>
        </div>

        <LeadsSubNav current="source" />

        {/* 2. TOP TOOLBAR */}
        <div className={cn(cardClassName(), 'w-full px-3 py-1.5 flex flex-wrap items-center justify-between gap-3')}>
          {/* Left Side */}
          <div className="flex items-center gap-2">
            <button type="button" className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted/60 rounded transition-colors" title="Expand/View">
              <Maximize2 className="h-4 w-4" />
            </button>
            {/* Search Input / Toggle Button */}
            {isSearchOpen ? (
              <div className="relative flex items-center animate-in fade-in duration-150">
                <Search className="h-3.5 w-3.5 absolute left-2.5 text-muted-foreground/70 pointer-events-none" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      if (searchQuery) setSearchQuery('')
                      else setIsSearchOpen(false)
                    }
                  }}
                  placeholder="Search leads by name, company, role..."
                  className="h-7 w-48 sm:w-64 pl-8 pr-7 text-xs rounded-md border border-primary/50 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all shadow-2xs"
                  autoFocus
                />
                {searchQuery ? (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery('')
                      searchInputRef.current?.focus()
                    }}
                    className="absolute right-1.5 p-0.5 text-muted-foreground hover:text-foreground rounded"
                    title="Clear search"
                  >
                    <X className="h-3 w-3" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsSearchOpen(false)}
                    className="absolute right-1.5 p-0.5 text-muted-foreground hover:text-foreground rounded"
                    title="Close search"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setIsSearchOpen(true)
                  setTimeout(() => searchInputRef.current?.focus(), 50)
                }}
                className={`p-1 rounded transition-colors ${
                  searchQuery
                    ? 'text-primary bg-primary/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                }`}
                title="Search leads"
              >
                <Search className="h-4 w-4" />
              </button>
            )}

            {searchQuery.trim() && (
              <span className="text-[11px] text-muted-foreground font-medium bg-muted/60 px-2 py-0.5 rounded-full">
                {filteredLeads.length} {filteredLeads.length === 1 ? 'lead' : 'leads'} found
              </span>
            )}
            <div className="h-4 w-px bg-border mx-0.5" />
            {/* Sort Control Dropdown */}
            <div className="relative" ref={sortDropdownRef}>
              <button
                type="button"
                onClick={() => setIsSortOpen((prev) => !prev)}
                className={`p-1 rounded transition-colors flex items-center gap-1.5 cursor-pointer ${
                  sortBy !== 'default'
                    ? 'text-primary bg-primary/10 border border-primary/30 font-medium'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                }`}
                title={sortBy === 'default' ? 'Sort leads' : `Sorted by: ${SORT_OPTIONS.find((o) => o.id === sortBy)?.label}`}
              >
                <ArrowUpDown className="h-4 w-4" />
                {sortBy !== 'default' && (
                  <span className="text-[11px] text-primary hidden sm:inline max-w-[120px] truncate">
                    {SORT_OPTIONS.find((o) => o.id === sortBy)?.label}
                  </span>
                )}
              </button>

              {isSortOpen && (
                <div className="absolute left-0 mt-1.5 w-64 rounded-lg border border-border/80 bg-popover text-popover-foreground shadow-lg z-50 p-1.5 animate-in fade-in-50 zoom-in-95 duration-150">
                  <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-border/50 text-[11px] font-semibold text-muted-foreground">
                    <span>SORT LEADS</span>
                    {sortBy !== 'default' && (
                      <button
                        type="button"
                        onClick={() => {
                          setSortBy('default')
                          setIsSortOpen(false)
                        }}
                        className="text-xs text-primary hover:underline font-normal cursor-pointer"
                      >
                        Reset
                      </button>
                    )}
                  </div>

                  <div className="py-1 flex flex-col gap-0.5 max-h-72 overflow-y-auto">
                    {SORT_OPTIONS.map((opt) => {
                      const isSelected = sortBy === opt.id
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => {
                            setSortBy(opt.id)
                            setIsSortOpen(false)
                          }}
                          className={`w-full flex items-center justify-between px-2.5 py-1.5 text-xs rounded-md transition-colors text-left cursor-pointer ${
                            isSelected
                              ? 'bg-primary/10 text-primary font-semibold'
                              : 'text-foreground hover:bg-muted/60'
                          }`}
                        >
                          <span>{opt.label}</span>
                          {isSelected && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
            <button type="button" className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted/60 rounded transition-colors" title="View Settings">
              <SlidersHorizontal className="h-4 w-4" />
            </button>
            <div className="h-4 w-px bg-border mx-0.5" />
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border/80 bg-background/70 text-xs font-medium text-foreground shadow-xs">
              <ListFilter className="h-3.5 w-3.5 text-muted-foreground" />
              <span>Status / Lead Source</span>
            </div>
          </div>
        </div>
      </div>

      {/* 3. MAIN BOARD MATRIX / GRID */}
      <div className={cn(cardClassName(), 'w-full overflow-hidden flex flex-col')}>
        <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-220px)] min-h-[460px] custom-scrollbar relative">
          <div className="min-w-[1300px] w-full">
            {/* Table Header Row: Status Columns (Sticky Top) */}
            <div
              className="sticky top-0 z-20 grid bg-muted border-b border-border/70 text-xs font-semibold"
              style={{ gridTemplateColumns: gridTemplate }}
            >
              {/* Corner Source Label Cell (Sticky Top & Left) */}
              <div className="sticky left-0 z-30 p-3 border-r border-border/60 text-muted-foreground font-semibold flex items-center bg-muted">
                Lead Source
              </div>
              {/* Status Columns (from configured lead statuses) */}
              {leadStatuses.map((col, index) => (
                <div
                  key={col.id}
                  className={`p-3 flex items-center gap-2 bg-muted ${
                    index < leadStatuses.length - 1 ? 'border-r border-border/60' : ''
                  }`}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-xs shrink-0 inline-block"
                    style={{ backgroundColor: col.color || '#6366f1' }}
                  />
                  <span className="font-bold text-foreground text-sm tracking-tight">{col.name}</span>
                </div>
              ))}
            </div>

            {/* If search query has 0 matches across the entire board */}
            {searchQuery.trim() && filteredLeads.length === 0 ? (
              <div className="py-16 px-4 text-center flex flex-col items-center justify-center gap-2 bg-background">
                <Search className="h-8 w-8 text-muted-foreground/40 mb-1" />
                <p className="text-sm font-semibold text-foreground">No leads found matching "{searchQuery}"</p>
                <p className="text-xs text-muted-foreground max-w-sm">
                  We couldn't find any leads matching your search criteria across any lead source or status.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('')
                    searchInputRef.current?.focus()
                  }}
                  className="mt-2 text-xs font-medium text-primary hover:underline cursor-pointer"
                >
                  Clear search query
                </button>
              </div>
            ) : (
              /* Matrix Rows: Source Rows */
              <div className="divide-y divide-border/60">
                {displaySources.map((sourceRow) => {
                  const sourceName = sourceRow.name
                  return (
                  <div
                    key={sourceRow.id}
                    className="grid items-stretch min-h-[100px] bg-background hover:bg-muted/5 transition-colors"
                    style={{ gridTemplateColumns: gridTemplate }}
                  >
                    {/* Row Header: Source Column (Sticky Left) */}
                    <div className="sticky left-0 z-10 p-3.5 border-r border-border/60 flex items-start gap-2 bg-card font-medium text-xs text-foreground select-none">
                      <List className="h-3.5 w-3.5 text-muted-foreground/70 shrink-0 mt-0.5" />
                      <span className="font-semibold text-foreground/90">{sourceName}</span>
                    </div>

                    {/* One cell per configured status */}
                    {leadStatuses.map((statusCol, index) => {
                      const leadsInCell = filteredLeads.filter(
                        (l) => l.source === sourceName && l.status === statusCol.name
                      )

                    return (
                      <div
                        key={statusCol.id}
                        className={`p-2.5 flex flex-col gap-2 relative max-h-[380px] overflow-y-auto custom-scrollbar ${
                          index < leadStatuses.length - 1 ? 'border-r border-border/60' : ''
                        }`}
                      >
                        {leadsInCell.map((lead) => {
                          const displayTitle = lead.company
                            ? `${lead.name} (${lead.company})`
                            : lead.name

                          return (
                            <div
                              key={lead.id}
                              onClick={() => handleCardClick(lead)}
                              className={cn(cardClassName({ variant: 'inset', interactive: true }), 'w-full border-l-[3.5px] p-2.5 transition-all cursor-pointer flex flex-col gap-2 group')}
                              style={{ borderLeftColor: statusCol.color || '#6366f1' }}
                            >
                              {/* Title */}
                              <div className="font-semibold text-xs text-foreground leading-snug break-words group-hover:text-primary transition-colors">
                                {displayTitle}
                              </div>

                              {/* CRM Contact */}
                              {lead.contactPerson && (
                                <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded border border-border/70 bg-muted/30 text-[11px] text-foreground/85 font-medium w-fit">
                                  <Contact className="h-3 w-3 text-muted-foreground shrink-0" />
                                  <span>{lead.contactPerson}</span>
                                </div>
                              )}

                              {/* Badges */}
                              <div className="flex flex-wrap items-center gap-1.5">
                                <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-border/70 bg-background text-[11px] font-medium text-foreground/80 shadow-2xs">
                                  {getInterestIcon(lead.interest)}
                                  <span>{lead.interest}</span>
                                </div>
                                <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-border/70 bg-background text-[11px] font-medium text-foreground/80 shadow-2xs">
                                  {getSourceIcon(lead.source)}
                                  <span>{lead.source}</span>
                                </div>
                                {typeof lead.value === 'number' && lead.value > 0 && (
                                  <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-950/20 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 shadow-2xs">
                                    <span>₹{lead.value.toLocaleString('en-IN')}</span>
                                  </div>
                                )}
                              </div>

                              {/* Bottom Details */}
                              <div className="flex items-center justify-between mt-0.5 pt-1">
                                <div className="flex items-center gap-1">
                                  {lead.hasAvatars && (
                                    <div className="flex items-center -space-x-1">
                                      <div
                                        className="h-[18px] w-[18px] rounded-full bg-purple-600 text-white text-[9px] font-bold flex items-center justify-center ring-1 ring-background shadow-xs"
                                        title="Assigned Rep"
                                      >
                                        {lead.contactPerson ? lead.contactPerson.slice(0, 2).toUpperCase() : lead.name.slice(0, 2).toUpperCase()}
                                      </div>
                                      <div
                                        className="h-[18px] w-[18px] rounded-full bg-indigo-500 text-white text-[9px] font-bold flex items-center justify-center ring-1 ring-background shadow-xs"
                                        title="User Indicator"
                                      >
                                        <User className="h-2.5 w-2.5" />
                                      </div>
                                    </div>
                                  )}
                                </div>
                                {lead.number && (
                                  <span className="text-[11px] font-mono text-muted-foreground/80 font-medium ml-auto">
                                    {lead.number}
                                  </span>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
                )
              })}
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Lead Detail Modal */}
      <LeadDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        lead={selectedLeadData}
        onUpdate={handleLeadUpdate}
        onDeleted={(id) => setLeads((prev) => prev.filter((l) => l.id !== id))}
        currentSectionName="Leads By Source"
        currentSectionHref="/app/leads/source"
      />

      {/* Add Lead Modal */}
      <AddLeadModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onLeadCreated={handleLeadCreated}
        initialSource="Trade Show"
      />
    </div>
  )
}
